import { useCallback, useState, useEffect } from 'react'

const STORAGE_KEY = 'makemerry_saved_items'

function loadSavedItems() {
    try {
        const stored = localStorage.getItem(STORAGE_KEY)
        return stored ? JSON.parse(stored) : []
    } catch (error) {
        console.error('Failed to load saved items:', error)
        return []
    }
}

function saveSavedItems(items) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
    } catch (error) {
        console.error('Failed to save items:', error)
    }
}

export function useSavedItems() {
    const [savedItems, setSavedItems] = useState(() => {
        return loadSavedItems()
    })

    // Save to localStorage
    useEffect(() => {
        saveSavedItems(savedItems)
    }, [savedItems])

    // URL update logic removed - no longer updating URL when saving items

    const saveItem = useCallback(item => {
        setSavedItems(prev => {
            // Check if already saved
            if (prev.some(saved => saved.id === item.id)) {
                return prev
            }
            return [...prev, { ...item, savedAt: Date.now() }]
        })
    }, [])

    const unsaveItem = useCallback(itemId => {
        setSavedItems(prev => prev.filter(item => item.id !== itemId))
    }, [])

    const toggleSaveItem = useCallback(
        itemOrId => {
            // Handle both item object and item ID string
            const itemId = typeof itemOrId === 'string' ? itemOrId : itemOrId?.id
            const isItemObject = typeof itemOrId === 'object' && itemOrId !== null

            // Guard against invalid input
            if (!itemId) {
                console.error('toggleSaveItem called with invalid item:', itemOrId)
                return
            }

            setSavedItems(prev => {
                const existingIndex = prev.findIndex(saved => saved.id === itemId)
                if (existingIndex !== -1) {
                    // If it exists and we have a full item object with data
                    if (isItemObject) {
                        const existing = prev[existingIndex]
                        // Check if it's a placeholder (fromUrl: true) that needs updating
                        if (existing.fromUrl && itemOrId.name !== 'Loading...') {
                            // Replace placeholder with real data
                            const updated = [...prev]
                            updated[existingIndex] = { ...itemOrId, savedAt: existing.savedAt }
                            return updated
                        }
                    }
                    // Otherwise, remove it (works for both item object and ID string)
                    return prev.filter(saved => saved.id !== itemId)
                }
                // Add new item (only if we have a full object)
                if (isItemObject) {
                    return [...prev, { ...itemOrId, savedAt: Date.now() }]
                }
                // If we only have an ID and item doesn't exist, ignore
                return prev
            })
        },
        [],
    )

    const isSaved = useCallback(
        itemId => {
            return savedItems.some(item => item.id === itemId)
        },
        [savedItems],
    )

    const clearSavedItems = useCallback(() => {
        setSavedItems([])
    }, [])

    return {
        savedItems,
        saveItem,
        unsaveItem,
        toggleSaveItem,
        isSaved,
        clearSavedItems,
    }
}
